'use strict';
/**
 * hipergas.utils.js  v3
 * Constantes, helpers, cálculos de densidad y simulador
 * Fechas en formato YYYY-MM-DDTHH:mm:ss (sin espacios) para compatibilidad con NPM/proxy
 */

export const TANKS = [
    { id: 1, label: 'Tanque 1', color: '#22C55E' },
    { id: 2, label: 'Tanque 2', color: '#38BDF8' },
    { id: 3, label: 'Tanque 3', color: '#FB923C' },
    { id: 4, label: 'Tanque 4', color: '#A78BFA' },
];

// ── Defaults locales — se reemplazan con los de la BD ────────────────────────
export const DEFAULT_CONFIG = {
    tanque_id: null,
    label: 'Tanque',
    capacidad_lts: 548250,
    producto: 'propano',
    densidad_override: null,
    unidad_display: 'tn',
    nivel_critico: 15,
    nivel_bajo: 25,
    nivel_alto: 90,
    temp_min: 0,
    temp_max: 35,
    presion_min: 0,
    presion_max: 4.5,
};

export function defaultConfigs() {
    return Object.fromEntries(
        TANKS.map((t) => [t.id, { ...DEFAULT_CONFIG, tanque_id: t.id, label: t.label }])
    );
}

// ── Densidad por producto y temperatura ──────────────────────────────────────
/**
 * calcDensidad — Densidad de GLP líquido en función de la temperatura
 *
 * Fuente: NIST WebBook (webbook.nist.gov) — propiedades termodinámicas
 * del propano (CAS 74-98-6) y butano (CAS 106-97-8) en fase líquida saturada.
 *
 * Correlación lineal derivada de tablas NIST, válida para:
 *   Propano:  -10°C a +40°C   (rango operativo típico de tanques estacionarios)
 *   Butano:   -5°C  a +40°C
 *
 * Fórmula:  ρ (kg/litro) = (A - B × T) / 1000
 *   Propano:  A = 581.8  B = 1.934
 *   Butano:   A = 638.5  B = 1.840
 *
 * Referencia normativa:
 *   - API MPMS Chapter 11 (Manual of Petroleum Measurement Standards)
 *   - ISO 91:2017 — Petroleum and related products, measurement tables
 *
 * Nota: la presión se omite porque los líquidos son casi incompresibles.
 * Su efecto sobre la densidad a presiones normales de operación es < 0.1%.
 *
 * @param {object} cfg    configuración del tanque (producto, densidad_override)
 * @param {number} tempC  temperatura actual del tanque en °C
 * @returns {number}      densidad en kg/litro
 */
export function calcDensidad(cfg, tempC) {
    // Si el operador configuró una densidad manual > 0, tiene prioridad
    if (cfg.densidad_override && Number(cfg.densidad_override) > 0)
        return Number(cfg.densidad_override);

    const t = Number(tempC) || 15; // fallback 15°C si no hay temperatura

    if (cfg.producto === 'propano') return (581.8 - 1.934 * t) / 1000;
    if (cfg.producto === 'butano') return (638.5 - 1.840 * t) / 1000;

    return 0.51; // producto 'otro' — densidad genérica de referencia
}

// ── Cálculo de masa disponible ────────────────────────────────────────────────
export function calcMasa(cfg, nivel, tempC) {
    const densidad = calcDensidad(cfg, tempC);
    const ltsLlenos = (cfg.capacidad_lts || 0) * (nivel / 100);
    const kg = ltsLlenos * densidad;

    switch (cfg.unidad_display) {
        case 'tn': return { valor: (kg / 1000).toFixed(2), unidad: 'tn' };
        case 'kg': return { valor: Math.round(kg).toLocaleString('es-AR'), unidad: 'kg' };
        case 'lts': return { valor: Math.round(ltsLlenos).toLocaleString('es-AR'), unidad: 'lts' };
        default: return { valor: (kg / 1000).toFixed(2), unidad: 'tn' };
    }
}

// ── Status del tanque ─────────────────────────────────────────────────────────
export function getTankStatus(tank, current, cfg) {
    const nivel = current[`TK_${tank.id}`];
    const temp = current[`TEMP_${tank.id}`];
    const presion = current[`PRESION_${tank.id}`];
    if (nivel <= cfg.nivel_critico || presion > cfg.presion_max) return 'danger';
    if (nivel <= cfg.nivel_bajo || temp > cfg.temp_max || temp < cfg.temp_min) return 'warn';
    return 'ok';
}

export function statusColor(status) {
    if (status === 'danger') return '#EF4444';
    if (status === 'warn') return '#F59E0B';
    return '#22C55E';
}

export function statusLabel(status) {
    if (status === 'danger') return 'CRÍTICO';
    if (status === 'warn') return 'BAJO';
    return 'ONLINE';
}

// ── Simulador ─────────────────────────────────────────────────────────────────
function rnd(v, d) { return parseFloat((v + (Math.random() - 0.5) * d).toFixed(2)); }

export function makeSample(prev) {
    return {
        id: (prev?.id || 8906) + 1,
        fecha: new Date().toLocaleTimeString('es-AR', { hour12: false }),
        TK_1: prev ? Math.max(5, Math.min(100, prev.TK_1 + (Math.random() > 0.55 ? 1 : -1))) : 45,
        TK_2: prev ? Math.max(5, Math.min(100, prev.TK_2 + (Math.random() > 0.60 ? 1 : -1))) : 20,
        TK_3: prev ? Math.max(5, Math.min(100, prev.TK_3 + (Math.random() > 0.50 ? 1 : -1))) : 68,
        TK_4: prev ? Math.max(5, Math.min(100, prev.TK_4 + (Math.random() > 0.45 ? 1 : -1))) : 32,
        TEMP_1: prev ? rnd(prev.TEMP_1, 0.4) : 9.4,
        TEMP_2: prev ? rnd(prev.TEMP_2, 0.4) : 15.7,
        TEMP_3: prev ? rnd(prev.TEMP_3, 0.4) : 14.6,
        TEMP_4: prev ? rnd(prev.TEMP_4, 0.4) : 11.2,
        PRESION_1: prev ? rnd(prev.PRESION_1, 0.04) : 0.91,
        PRESION_2: prev ? rnd(prev.PRESION_2, 0.10) : 3.10,
        PRESION_3: prev ? rnd(prev.PRESION_3, 0.04) : 1.60,
        PRESION_4: prev ? rnd(prev.PRESION_4, 0.04) : 2.20,
    };
}

export function buildHistory(n = 40) {
    const h = [makeSample(null)];
    for (let i = 1; i < n; i++) h.push(makeSample(h[i - 1]));
    return h;
}

export function checkAlarms(current, prev, configs, enqueueSnackbar, setAlarms) {
    const hora = new Date().toLocaleTimeString('es-AR', { hour12: false });
    const add = (tank, msg, sev) => {
        setAlarms((a) => [{ id: Date.now() + Math.random(), hora, tank, msg, sev }, ...a].slice(0, 60));
        enqueueSnackbar(`${tank}: ${msg}`, {
            variant: sev === 'danger' ? 'error' : 'warning',
            autoHideDuration: 5000,
        });
    };
    TANKS.forEach((tank) => {
        const cfg = configs[tank.id];
        if (!cfg) return;
        const nivel = current[`TK_${tank.id}`];
        const temp = current[`TEMP_${tank.id}`];
        const pres = current[`PRESION_${tank.id}`];
        const pN = prev?.[`TK_${tank.id}`];
        const pT = prev?.[`TEMP_${tank.id}`];
        const pP = prev?.[`PRESION_${tank.id}`];

        if (nivel <= cfg.nivel_critico && (!pN || pN > cfg.nivel_critico))
            add(tank.label, `Nivel CRÍTICO — ${nivel}%`, 'danger');
        else if (nivel <= cfg.nivel_bajo && (!pN || pN > cfg.nivel_bajo) && nivel > cfg.nivel_critico)
            add(tank.label, `Nivel BAJO — ${nivel}%`, 'warn');
        else if (nivel >= cfg.nivel_alto && (!pN || pN < cfg.nivel_alto))
            add(tank.label, `Nivel ALTO — ${nivel}%`, 'warn');

        if (temp > cfg.temp_max && (!pT || pT <= cfg.temp_max))
            add(tank.label, `Temperatura alta — ${temp}°C`, 'warn');
        if (temp < cfg.temp_min && (!pT || pT >= cfg.temp_min))
            add(tank.label, `Temperatura baja — ${temp}°C`, 'warn');
        if (pres > cfg.presion_max && (!pP || pP <= cfg.presion_max))
            add(tank.label, `Presión alta — ${pres} bar`, 'danger');
    });
}

export const VARDEF = {
    nivel: { label: 'Nivel', unit: '%', keys: TANKS.map((t) => `TK_${t.id}`), min: 0, max: 100 },
    temp: { label: 'Temperatura', unit: '°C', keys: TANKS.map((t) => `TEMP_${t.id}`), min: 0, max: 40 },
    presion: { label: 'Presión', unit: 'bar', keys: TANKS.map((t) => `PRESION_${t.id}`), min: 0, max: 5 },
};